/**
 * Atlas-wide date/datetime input that always renders in NZ wall-clock and
 * 24-hour format, independent of the browser/OS locale.
 *
 * Why bespoke instead of `<input type="datetime-local">`/`<input type="time">`:
 * the native widgets honour the OS locale for display — a US-locale browser
 * shows AM/PM regardless of `<html lang>` or `Intl.DateTimeFormat` options.
 * We need fixed 24-hour visuals to match the dispatch dashboard's 24-hour
 * timeline.
 *
 * Composition:
 *  - Text input showing `DD/MMM/YYYY` or `DD/MMM/YYYY HH:mm` (keyboard-typeable,
 *    parsed via `parseDDMMYYYY`).
 *  - Calendar icon → popover with shadcn `Calendar` for click-to-pick day.
 *  - When `withTime`, the popover footer has two zero-padded number inputs
 *    (HH 00–23, MM 00–59) — always 24-hour, locale-immune.
 */
import React from 'react';
import { Input } from '@/components/ui/input';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { fmtDate, fmtDateTime, parseDDMMYYYY } from '@/lib/format-date';

// Pull the NZ wall-clock components out of a Date so the popover time inputs
// reflect the depot's clock rather than the viewer's browser-local hour.
const NZ_PARTS_FMT = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Pacific/Auckland', year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', hour12: false,
});
function nzParts(d: Date): { year: number; month: number; day: number; hour: number; minute: number } {
  const parts = NZ_PARTS_FMT.formatToParts(d);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  const h = get('hour');
  return { year: get('year'), month: get('month'), day: get('day'), hour: h === 24 ? 0 : h, minute: get('minute') };
}

/** Reconstruct a UTC Date from NZ wall-clock components (DST-aware). */
function nzWallToUtc(y: number, mo: number, d: number, h: number, mi: number): Date {
  let cand = new Date(Date.UTC(y, mo, d, h, mi, 0, 0));
  for (let i = 0; i < 2; i++) {
    const p = nzParts(cand);
    const drift = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, 0) - Date.UTC(y, mo, d, h, mi, 0);
    if (drift === 0) break;
    cand = new Date(cand.getTime() - drift);
  }
  return cand;
}

/**
 * Locale-immune 24-hour time-only input bound to an `HH:mm` string. Two
 * zero-padded number inputs (HH 00–23, MM 00–59) — no native
 * `<input type="time">`, so US-locale browsers can't render AM/PM. Empty
 * value emits `''`. Optional `min` / `max` ("HH:mm") gate the on-blur commit.
 */
export function NzTimeInput({ value, onChange, ariaLabel, invalid, min, max, disabled, className }: {
  value: string;
  onChange: (v: string) => void;
  ariaLabel?: string;
  invalid?: boolean;
  min?: string;
  max?: string;
  disabled?: boolean;
  className?: string;
}) {
  const split = (v: string): [string, string] => {
    const m = /^(\d{1,2}):(\d{2})$/.exec(v.trim());
    return m ? [m[1].padStart(2, '0'), m[2]] : ['', ''];
  };
  const [hhRaw, mmRaw] = split(value);
  const [hh, setHh] = React.useState(hhRaw);
  const [mm, setMm] = React.useState(mmRaw);
  React.useEffect(() => { const [a, b] = split(value); setHh(a); setMm(b); }, [value]);

  const commit = (rawH: string, rawM: string) => {
    if (rawH === '' && rawM === '') { onChange(''); return; }
    let h = Math.max(0, Math.min(23, parseInt(rawH, 10) || 0));
    let m = Math.max(0, Math.min(59, parseInt(rawM, 10) || 0));
    let next = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    // Clamp to min / max if provided
    if (min && next < min) next = min;
    if (max && next > max) next = max;
    setHh(next.slice(0, 2));
    setMm(next.slice(3, 5));
    onChange(next);
  };

  return (
    <div className={cn('flex items-center gap-1 tabular-nums', className)}>
      <Input
        aria-label={`${ariaLabel ?? 'Time'} hours (00-23)`}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={2}
        value={hh}
        disabled={disabled}
        onChange={(e) => setHh(e.target.value.replace(/[^0-9]/g, '').slice(0, 2))}
        onBlur={() => commit(hh, mm)}
        aria-invalid={invalid || undefined}
        className={cn('w-14 text-center', invalid && 'border-destructive focus-visible:ring-destructive')}
      />
      <span className="text-muted-foreground">:</span>
      <Input
        aria-label={`${ariaLabel ?? 'Time'} minutes (00-59)`}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={2}
        value={mm}
        disabled={disabled}
        onChange={(e) => setMm(e.target.value.replace(/[^0-9]/g, '').slice(0, 2))}
        onBlur={() => commit(hh, mm)}
        aria-invalid={invalid || undefined}
        className={cn('w-14 text-center', invalid && 'border-destructive focus-visible:ring-destructive')}
      />
    </div>
  );
}

export interface NzDateTimeInputProps {
  value: string | Date | null;
  onChange: (d: Date | null) => void;
  withTime: boolean;
  ariaLabel: string;
  invalid?: boolean;
  className?: string;
}

export function NzDateTimeInput({ value, onChange, withTime, ariaLabel, invalid, className }: NzDateTimeInputProps) {
  const incoming = withTime ? fmtDateTime(value, '') : fmtDate(value, '');
  const [text, setText] = React.useState(incoming);
  const [open, setOpen] = React.useState(false);
  const lastSyncedRef = React.useRef(incoming);
  // Sync prop changes (e.g. form reset) into the text state without
  // clobbering in-progress typing.
  React.useEffect(() => {
    if (incoming !== lastSyncedRef.current) {
      lastSyncedRef.current = incoming;
      setText(incoming);
    }
  }, [incoming]);

  const commit = (s: string) => {
    if (s.trim() === '') { onChange(null); return; }
    const d = parseDDMMYYYY(s);
    if (d) onChange(d);
  };

  // Commit as-you-type once the text is a COMPLETE valid date(-time), so
  // form validation (e.g. "End must be after Start" disabling Save) reacts
  // immediately instead of waiting for blur. Partial strings don't parse —
  // no-op. With `withTime`, require the HH:mm part before live-committing:
  // a bare date parses as midnight, which would yank the value around while
  // the user is still editing the time digits (blur keeps the old lenient
  // behavior of defaulting a missing time to 00:00).
  const liveCommit = (s: string) => {
    if (s.trim() === '') { lastSyncedRef.current = ''; onChange(null); return; }
    if (withTime && !/\d{1,2}:\d{2}\s*$/.test(s)) return;
    const d = parseDDMMYYYY(s);
    if (!d) return;
    // Pre-sync the ref so the incoming-prop effect doesn't reformat the text
    // (and jump the caret) while the user is still typing; blur re-formats.
    lastSyncedRef.current = withTime ? fmtDateTime(d, '') : fmtDate(d, '');
    onChange(d);
  };

  const currentDate = (() => {
    if (!value) return undefined;
    const d = value instanceof Date ? value : new Date(value);
    return Number.isNaN(d.getTime()) ? undefined : d;
  })();

  // NZ-local parts of the current value — drives both the Calendar default
  // selection AND the HH/MM popover inputs.
  const nz = currentDate ? nzParts(currentDate) : null;

  // ---- Time pickers (NZ wall-clock, 24-hour, locale-immune) ----------------
  const [hh, setHh] = React.useState<string>(nz ? String(nz.hour).padStart(2, '0') : '09');
  const [mm, setMm] = React.useState<string>(nz ? String(nz.minute).padStart(2, '0') : '00');
  React.useEffect(() => {
    if (nz) {
      setHh(String(nz.hour).padStart(2, '0'));
      setMm(String(nz.minute).padStart(2, '0'));
    }
  }, [nz?.hour, nz?.minute]);

  const commitTime = (nextHh: string, nextMm: string) => {
    const h = Math.max(0, Math.min(23, parseInt(nextHh, 10) || 0));
    const m = Math.max(0, Math.min(59, parseInt(nextMm, 10) || 0));
    const base = nz ?? (() => { const t = new Date(); const p = nzParts(t); return p; })();
    onChange(nzWallToUtc(base.year, base.month - 1, base.day, h, m));
  };

  return (
    <div className={cn('relative', className)}>
      <Input
        aria-label={ariaLabel}
        type="text"
        placeholder={withTime ? 'DD/MMM/YYYY HH:mm' : 'DD/MMM/YYYY'}
        value={text}
        onChange={(e) => { setText(e.target.value); liveCommit(e.target.value); }}
        onBlur={(e) => commit(e.target.value)}
        aria-invalid={invalid || undefined}
        className={cn('pr-9', invalid && 'border-destructive focus-visible:ring-destructive')}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Open calendar"
            className="absolute right-1 top-1/2 -translate-y-1/2 p-1.5 rounded hover:bg-muted text-muted-foreground"
          >
            <CalendarIcon className="h-4 w-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-auto p-0">
          <Calendar
            mode="single"
            selected={currentDate}
            defaultMonth={currentDate}
            onSelect={(d) => {
              if (!d) { onChange(null); return; }
              // Calendar yields a Date at the picked day's browser-local
              // midnight. Pull the calendar-picked Y/M/D and combine with
              // current time-of-day (or `withTime ? 09:00 : 00:00` default)
              // to build the proper NZ wall-clock UTC instant.
              const y = d.getFullYear();
              const mo = d.getMonth();
              const dd = d.getDate();
              if (withTime) {
                const h = Math.max(0, Math.min(23, parseInt(hh, 10) || 0));
                const m = Math.max(0, Math.min(59, parseInt(mm, 10) || 0));
                onChange(nzWallToUtc(y, mo, dd, h, m));
              } else {
                onChange(nzWallToUtc(y, mo, dd, 0, 0));
                setOpen(false);
              }
            }}
            initialFocus
          />
          {withTime && (
            <div className="flex items-center justify-between gap-2 border-t p-3">
              <span className="text-sm text-muted-foreground">Time</span>
              <div className="flex items-center gap-1 tabular-nums">
                <Input
                  aria-label="Hours (00-23)"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={2}
                  value={hh}
                  onChange={(e) => {
                    const v = e.target.value.replace(/[^0-9]/g, '').slice(0, 2);
                    setHh(v);
                    // Two digits = complete hour — commit live so validation
                    // reacts without waiting for blur.
                    if (v.length === 2) commitTime(v, mm);
                  }}
                  onBlur={() => {
                    const padded = String(Math.max(0, Math.min(23, parseInt(hh, 10) || 0))).padStart(2, '0');
                    setHh(padded);
                    commitTime(padded, mm);
                  }}
                  className="w-14 text-center"
                />
                <span className="text-muted-foreground">:</span>
                <Input
                  aria-label="Minutes (00-59)"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={2}
                  value={mm}
                  onChange={(e) => {
                    const v = e.target.value.replace(/[^0-9]/g, '').slice(0, 2);
                    setMm(v);
                    if (v.length === 2) commitTime(hh, v);
                  }}
                  onBlur={() => {
                    const padded = String(Math.max(0, Math.min(59, parseInt(mm, 10) || 0))).padStart(2, '0');
                    setMm(padded);
                    commitTime(hh, padded);
                  }}
                  className="w-14 text-center"
                />
              </div>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
